import { ChartOptions, SeriesMapOptions, SeriesMapbubbleOptions } from 'highcharts/highcharts';
import cloneDeep from 'lodash.clonedeep';
import {
  Geometry,
  ImportedMap,
  MapType,
} from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { Color } from 'types/basic.types';
import { parseStrictNumber } from 'utils/number';
import { getRandomInt } from 'utils/random';
import { CustomLegendRender, validateDataCell } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartDataSeries } from 'widgets/ChartWidgets/ChartWidget.types';
import {
  FONT_SIZE_MULTIPLIER,
  ITEM_MARGIN_BOTTOM_MULTIPLIER,
} from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.config';
import { HighchartsOptions } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommon.types';
import { useWidget } from 'widgets/sdk';

import {
  DEFAULT_BUBBLE_BACKGROUND_COLOR,
  DEFAULT_BUBBLE_COLOR,
  DEFAULT_COLOR_STEPS,
  DEFAULT_MAP_WIDGET_DATA,
  DEFAULT_MONOCHROME_BACKGROUND_COLOR,
  PRESET_PALETTE,
} from './MapWidget.config';
import {
  ColorPaletteName,
  CustomSeriesMapOptions,
  DefaultMapWidgetData,
  MapChartData,
  MapLegendType,
  MapWidgetData,
  Range,
  Region,
  SeriesMetaColorConfig,
} from './MapWidget.types';

/**
 * Generates default map widget data based on the given map data.
 *
 * @param {ImportedMap | undefined} mapData - The map data used to generate the widget data.
 * @param {object} position - The position of the widget on the screen.
 * @param {number} position.top - The top position in pixels.
 * @param {number} position.left - The left position in pixels.
 * @param {string} region - The region associated with the map data.
 *
 * @returns {DefaultMapWidgetData | undefined} The generated default map widget data, or undefined if no map data is provided.
 */
export const generateDefaultData = (
  mapData: ImportedMap | undefined,
  position: { top: number; left: number },
  region: string,
): DefaultMapWidgetData | undefined => {
  if (!mapData) return;
  const geometries = getGeometries(mapData);
  const generatedRegions = generateTableData(geometries ? geometries : []);
  const chartData = getChartData(generatedRegions);

  const currentSeriesData = [
    {
      name: 'Name',
      data: getMapRegionNames(generatedRegions),
    },
    {
      name: 'Value',
      data: getValuesFromKeyValuePairs(chartData),
    },
  ];
  const { seriesValues } = validateSeriesData(currentSeriesData);
  const dataClasses = generateDataClasses(DEFAULT_COLOR_STEPS, seriesValues);

  return {
    ...DEFAULT_MAP_WIDGET_DATA,
    widgetData: {
      ...DEFAULT_MAP_WIDGET_DATA.widgetData,
      topPx: position.top,
      leftPx: position.left,
      importedMap: mapData,
      region,
      seriesMeta: {
        ...DEFAULT_MAP_WIDGET_DATA.widgetData.seriesMeta,
        dataClasses: dataClasses,
      },
      seriesData: currentSeriesData,
    },
  };
};

/**
 * Custom hook for generating Highcharts map options based on widget data.
 * @returns The Highcharts options for the map.
 */
export const useHighchartsMapOptions = (): HighchartsOptions => {
  const widgetData = useWidget<MapWidgetData>();
  const { widthPx, heightPx } = widgetData;

  const defaultOptions: HighchartsOptions = {
    chart: {
      width: widthPx,
      height: heightPx,
      backgroundColor: 'transparent',
      animation: false,
      reflow: false,
    },
    // TODO: https://www.highcharts.com/docs/accessibility/accessibility-module
    accessibility: {
      enabled: false,
    },
    credits: { enabled: false },
    mapNavigation: { enabled: false },
    tooltip: { enabled: false },
    exporting: { enabled: false },
    mapView: {
      insetOptions: {
        borderWidth: 0,
      },
    },
  };

  return getHighchartsMapConfig(defaultOptions, widgetData);
};

/**
 * Returns an array of colors based on the desired number of color steps and palette color name.
 *
 * @param {Color[]} currentColors - The original array of colors.
 * @param {ColorPaletteName} paletteColorName - The name of the color palette.
 * @param {number} colorSteps - The desired number of color steps.
 * @returns {Color[]} The array of colors.
 *
 * @example
 * // currentColors: ["#FFEDA0", "#FEB24C", "#F03B20"], paletteColorName: 'Yellow-Red", colorSteps: 5
 * // returns ["#FFEDA0", "#FEB24C", "#F03B20", "#F03B20", "#BD0026"]
 */
export const getUpdatedColors = (
  currentColors: Color[],
  paletteColorName: ColorPaletteName,
  colorSteps: number,
): Color[] => {
  // Todo: compare with growShrinkColorArray and see if it is better
  const colorAmount = currentColors.length;

  // If the new colorSteps count is larger, we need to add colors
  if (colorSteps > colorAmount) {
    const colorPalette = findColorsByLabel(paletteColorName);
    const colorsFromPalette = colorPalette[colorSteps];

    if (colorsFromPalette) {
      const additionalColors = colorsFromPalette.slice(colorAmount);
      return [...currentColors, ...additionalColors];
    }
  } else if (colorSteps < colorAmount) {
    // If the new colorSteps count is smaller, we remove colors
    return currentColors.slice(0, colorSteps);
  }

  // If it is the same, just return the original colors
  return currentColors;
};

/**
 * Generates data classes (ranges) based on the number of color steps and input values.
 *
 * @param {number} colorSteps - The desired number of color steps.
 * @param {number[] | undefined} values - The input values array.
 * @returns {Range[]} An array of data classes (ranges) based on the number of color steps.
 *
 * @example
 * // colorSteps: 5
 * // values: [10, 20, 30, 40, 50]
 * // returns: [
 * //   { from: undefined, to: 20 },
 * //   { from: 21, to: 40 },
 * //   { from: 41, to: 60 },
 * //   { from: 61, to: 80 },
 * //   { from: 81, to: undefined },
 * // ]
 */

export const generateDataClasses = (colorSteps: number, values: number[]): Range[] => {
  if (!values || values.length === 0) return [];

  // Removing NaN values so Math.min and Math.max won't break
  values = values.filter((number) => !isNaN(number));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  // We calculate the average size of each range
  const stepSize = range / colorSteps;
  const groups = [];
  let start = min;

  // Based on the number of steps, we create intermediate 'from' and 'to' objects
  for (let i = 0; i < colorSteps; i++) {
    const end = start + stepSize - 1;

    const isFirstRange = i === 0;
    const isLastRange = i === colorSteps - 1;
    // Design requirement: First 'from' value of the range should be undefined.
    // Design requirement: Last 'to' value of the range should be undefined;
    groups.push({ from: isFirstRange ? undefined : Math.ceil(start), to: isLastRange ? undefined : Math.ceil(end) });
    start = end + 1;
  }

  return groups;
};

/**
 * Validates and processes chart data series.
 *
 * @param {ChartDataSeries|undefined} seriesData - The chart data series to be validated.
 * @returns {{
 *   safeSeriesData: ChartDataSeries[], // An array of validated chart data series.
 *   seriesValues: value[] // An array of validated series values.
 * }} An object containing the validated chart data series and series values.
 */
export const validateSeriesData = (seriesData: ChartDataSeries | undefined) => {
  const nameSeries = seriesData?.find((series) => series?.name === 'Name');
  const nameData = nameSeries?.data ?? [];
  const valueSeries = seriesData?.find((series) => series?.name === 'Value');
  const valueData = valueSeries?.data ?? [];
  const validatedValues = valueData?.map((value) => validateDataCell(value));

  return {
    safeSeriesData: [
      { ...nameSeries, data: nameData },
      { ...valueSeries, data: validatedValues },
    ],
    seriesValues: validatedValues,
  };
};

/**
 * Gets the updated data classes based on the provided parameters.
 * If the `isCustomizeRangeOn` is true and the number of data classes does not match the newColorSteps,
 * the data classes will be adjusted using the `adjustDataClasses` function.
 * Otherwise, the data classes will be generated based on the newColorSteps and seriesData.
 *
 * @param {Range[]} dataClasses - The current data classes.
 * @param {number} newColorSteps - The desired number of color steps.
 * @param {boolean} isCustomizeRangeOn - Whether the user is customizing the color range.
 * @param {ChartDataSeries} seriesData - The chart data series.
 * @returns {Range[]} The updated data classes.
 */
export const getUpdatedDataClasses = (
  dataClasses: Range[] | undefined,
  newColorSteps: number,
  isCustomizeRangeOn: boolean,
  seriesData: ChartDataSeries,
): Range[] => {
  const { seriesValues } = validateSeriesData(seriesData);

  if (dataClasses && isCustomizeRangeOn && dataClasses.length !== newColorSteps) {
    return adjustDataClasses(dataClasses, newColorSteps);
  }
  return generateDataClasses(newColorSteps, seriesValues);
};

/**
 * Gets the Highcharts map configuration based on the default options and widget data.
 * @param defaultOptions The default Highcharts options.
 * @param widgetData The widget data used to configure the Highcharts map.
 * @returns The Highcharts map configuration.
 */
const getHighchartsMapConfig = (defaultOptions: Highcharts.Options, widgetData: MapWidgetData) => {
  const {
    importedMap,
    seriesData,
    series,
    legend,
    mapType,
    seriesMeta: {
      colors,
      dataClasses,
      colorSteps,
      bubbleColor = DEFAULT_BUBBLE_COLOR,
      bubbleBackgroundColor = DEFAULT_BUBBLE_BACKGROUND_COLOR,
      monochromeBackgroundColor = DEFAULT_MONOCHROME_BACKGROUND_COLOR,
    },
  } = widgetData;

  const options = defaultOptions;
  const geometries = getGeometries(importedMap);

  // We get the name: hc-key pairs here in useHighchartsMapOptions hook
  // instead of setting into MapWidgetData
  const regionNameKeyPairs = getRegionNameKeyPairs(geometries);

  const { safeSeriesData, seriesValues } = validateSeriesData(seriesData);
  const safeDataClasses = validateDataClasses(dataClasses, seriesValues, colorSteps);
  const updatedSeries = getUpdatedSeries(regionNameKeyPairs, safeSeriesData);

  // Todo: check if it is required to always get from default charts or it can be skipped entirely.
  const defaultChart = options.chart;
  options.chart = {
    map: importedMap,
    events: { render: CustomLegendRender },
    ...defaultChart,
  } as ChartOptions;

  const seriesWithUpdatedDataLabels = getDataLabelsUpdatedSeries(series);

  options.series = mergeSeriesOptions(seriesWithUpdatedDataLabels, updatedSeries, importedMap, mapType, {
    colors,
    bubbleColor,
    bubbleBackgroundColor,
    monochromeBackgroundColor,
  });

  const fontSize = parseStrictNumber(
    typeof legend.itemStyle.fontSize === 'string' ? legend.itemStyle.fontSize : legend.labelStyle.fontSize,
  );
  const symbolWidth = fontSize * FONT_SIZE_MULTIPLIER;
  const symbolHeight = fontSize * FONT_SIZE_MULTIPLIER;
  const itemMarginBottom = fontSize * ITEM_MARGIN_BOTTOM_MULTIPLIER; // ensure stacked up labels are spaced evenly
  const enhancedLegend = {
    ...legend,
    symbolWidth: symbolWidth,
    symbolHeight: symbolHeight,
    itemMarginBottom: itemMarginBottom,
  };

  options.legend = mergeLegendOptions(enhancedLegend, mapType, countDecimalPointsInValues(seriesValues));

  // Map widget design suggests text widget for title so it is not needed.
  options.title = undefined;

  options.colorAxis = {
    dataClassColor: 'category',
    dataClasses: getColorAxisDataClasses(safeDataClasses, seriesValues),
  };

  options.colors = mergeMapChartColors(series, colors, seriesValues);

  return options;
};

/**
 * Generates data classes for the color axis based on the provided data classes and series values.
 *
 * @param {Range[] | undefined} dataClasses - An array of data classes with range information, if available.
 * @param {(string | number)[]} seriesValues - An array of series values containing strings or numbers.
 * @returns {Array<{ name: string } | Range>} An array of data classes for the color axis.
 */
const getColorAxisDataClasses = (dataClasses: Range[] | undefined, seriesValues: (string | number)[]) => {
  // Use the nullish coalescing operator to handle the case when dataClasses is undefined or not an array.
  dataClasses = dataClasses ?? [];

  if (seriesValues.includes('')) {
    // If an empty string is present in seriesValues, add a special data class named "No data".
    return [{ name: 'No data' }, ...dataClasses];
  }
  // Otherwise, return the original dataClasses array.
  return [...dataClasses];
};

/**
 * Retrieves an array of region names from the given region array.
 * @param regions An array of regions.
 * @returns An array of region names.
 */
const getMapRegionNames = (regions: Region[]): string[] => regions.map((data) => data.name);

/**
 * Generates randomized table data from the given geometries.
 * @param geometries The array of geometries containing properties.
 * @returns An array of regions with name, key and value.
 * @example
 * // Returns an array with this structure.
 * [0]: {name: 'Alabama', key: 'us-al', value: '79'},
 * [1]: {name: 'Alaska', key: 'us-ak', value: '71'},
 * [2]: {name: 'Arizona', key: 'us-az', value: '22'}, ...
 */
const generateTableData = (geometries: Geometry[]): Region[] => {
  const regions: Region[] = [];
  geometries.forEach((geometry) => {
    if (geometry.properties['hc-key'] && geometry.properties['name']) {
      regions.push({
        name: geometry.properties['name'].toString(),
        key: geometry.properties['hc-key'].toString(),
        value: getRandomInt(1, 100).toString(),
      });
    }
  });
  regions.sort((a, b) => a.name.localeCompare(b.name));
  return regions;
};

/**
 * Extracts the Highcharts name-key pairs from the region array.
 * @param regions The array of regions containing highchartsKey and value properties.
 * @returns An array of Highcharts name-key pairs.
 * @example
 * // Returns an array with this structure.
 * [0]: ['Alabama', 'us-al'],
 * [1]: ['Alaska', 'us-ak'],
 * [2]: ['Arizona', 'us-az'], ...
 */
const getRegionNameKeyPairs = (geometries: Geometry[] | undefined): string[][] => {
  const regions: string[][] = [];
  if (!geometries) return [];
  geometries.forEach((geometry) => {
    if (geometry.properties['hc-key'] && geometry.properties['name']) {
      const currName = geometry.properties['name'].toString();
      const currKey = geometry.properties['hc-key'].toString();
      regions.push([currName, currKey]);
    }
  });
  regions.sort((a, b) => a[0].localeCompare(b[0]));
  return regions;
};

/**
 * Extracts the Highcharts key-value pairs from the region array.
 * @param regions The array of regions containing highchartsKey and value properties.
 * @returns An array of Highcharts key-value pairs.
 * @example
 * // Returns an array with this structure.
 * [0]: ['us-al', '79'],
 * [1]: ['us-ak', '71'],
 * [2]: ['us-az', '22'], ...
 */
const getChartData = (regions: Region[]): MapChartData => regions.map((data) => [data.key, data.value]);

/**
 * Extracts the values from an array of key-value pairs.
 * @param chartData The array of key-value pairs.
 * @returns An array of values extracted from the key-value pairs.
 * @example
 * // Returns an array with this structure.
 * [0]: '79',
 * [1]: '71',
 * [2]: '22', ...
 */
const getValuesFromKeyValuePairs = (chartData: MapChartData): (string | number)[] => {
  // Position 0 is the highcharts-key index, position 1 is the value index
  return chartData.map((valuePair) => valuePair[1]);
};

/**
 * Replaces the current chart data with incoming data, if available.
 * @param nameHcKeyPair The current chart data to be replaced.
 * @param seriesData The incoming data to replace the current chart data.
 * @returns The updated chart data.
 */
const getUpdatedSeries = (nameHcKeyPair: string[][], seriesData: ChartDataSeries) => {
  // If the incoming seriesData has no item, return empty array as well.
  if (seriesData.length === 0) return [];

  // Todo: making sure seriesData inherits properly from ChartDataSeries. See PR #1602
  const nameValueData = combineNameValueData(seriesData);
  const result = nameValueData.map((entry) => {
    const [name, value] = entry;
    if (name === '' || name === null || name === undefined) return [];

    const hcKeyEntry = nameHcKeyPair.find(([n]) => n.toLowerCase() === name.toString().toLowerCase());
    if (hcKeyEntry) {
      const [, hcKey] = hcKeyEntry;
      return [hcKey, typeof value === 'number' && !isNaN(value) ? value : null];
    }
    // Only return when name is valid
    return [name, typeof value === 'number' && !isNaN(value) ? value : null];
  });

  return result;
};

/**
 * Combines name and value data from the given series data into an array of name-value pairs.
 *
 * @param {ChartDataSeries} seriesData - The series data containing 'name' and 'value' entries.
 * @returns {Array<Array<string | number>>} The combined array of name-value pairs.
 */
const combineNameValueData = (seriesData: ChartDataSeries) => {
  /**
   * An array to hold the combined name-value pairs.
   * @type {Array<Array<string | number>>}
   */
  const combinedData = seriesData[0].data.map((name, index) => [name, seriesData[1].data[index]]);
  return combinedData;
};

/**
 * Merges the colors array with map chart options, including the null color if provided and an empty string is present in the `seriesValues`.
 *
 * @param {CustomSeriesMapOptions} series - The map series options.
 * @param {Color[] | undefined} colors - The array of colors, if available.
 * @param {(string | number)[]} seriesValues - The array of series values containing strings or numbers.
 * @returns {Array<Color | GradientColorObject | PatternObject>} The merged array of colors with map chart options.
 */
const mergeMapChartColors = (
  series: CustomSeriesMapOptions,
  colors: Color[] | undefined,
  seriesValues: (string | number)[],
) => {
  // Use the nullish coalescing operator to handle the case when dataClasses is undefined or not an array.
  colors = colors ?? [];
  if ('nullColor' in series && series.nullColor && seriesValues.includes('')) return [series.nullColor, ...colors];
  return [...colors];
};

// Todo: simplify this function even more after bubble and monochrome map release
/**
 * Merges the default series options with the updated series data and type to create an array of series options.
 *
 * @param {CustomSeriesMapOptions} defaultSeries - The default series options.
 * @param {(string | number | null)[][] | undefined} updatedSeries - The updated series data.
 * @param {ImportedMap} importedMap - The imported map data.
 * @param {MapType} mapType - The type of map being used.
 * @param {SeriesMetaColorConfig} seriesMetaColorConfig - The color configuration for the series.
 * @returns {(SeriesMapOptions | SeriesMapbubbleOptions)[]} The array of merged series options.
 *
 * @example
 * // defaultSeries: { data: [], type: 'map' }
 * // updatedSeries: [['A', 10], ['B', 20]]
 * // importedMap: {...} // An example of imported map data
 * // mapType: MapType.Bubble
 * // seriesMetaColorConfig: {...} // An example of series meta color configuration
 * // returns: [
 * //   { data: [['A', 10], ['B', 20]], colors, showInLegend: false, colorByPoint: true },
 * //   { data: [...], mapData: importedMap, type: 'mapbubble', ... }
 * // ]
 */
const mergeSeriesOptions = (
  defaultSeries: CustomSeriesMapOptions,
  updatedSeries: (string | number | null)[][] | undefined,
  importedMap: ImportedMap,
  mapType: MapType,
  seriesMetaColorConfig: SeriesMetaColorConfig,
): (SeriesMapOptions | SeriesMapbubbleOptions)[] => {
  const { colors, bubbleColor, bubbleBackgroundColor, monochromeBackgroundColor } = seriesMetaColorConfig;
  if (mapType === MapType.Choropleth) {
    return [
      {
        ...defaultSeries,
        data: updatedSeries,
        colors,
        showInLegend: false,
        colorByPoint: true,
      },
    ] as SeriesMapOptions[];
  } else if (mapType === MapType.Bubble) {
    return [
      {
        ...defaultSeries,
        data: updatedSeries,
        color: bubbleBackgroundColor,
        colorAxis: true,
        colorByPoint: false,
      },
      {
        ...defaultSeries,
        data: getBubbleMapData(updatedSeries),
        mapData: importedMap,
        type: 'mapbubble',
        minSize: '1%',
        maxSize: '10%',
        sizeBy: 'width',
        color: bubbleColor,
        colorAxis: true,
        animation: false,
      },
    ] as SeriesMapbubbleOptions[];
  } else if (mapType === MapType.Monochrome) {
    return [
      {
        ...defaultSeries,
        data: updatedSeries,
        color: monochromeBackgroundColor,
        colorAxis: true,
        colorByPoint: false,
      },
    ] as SeriesMapOptions[];
  }
  return [];
};

/**
 * Merges the provided legend options with additional options based on the map type.
 *
 * @param {MapLegendType} legend - The original legend options.
 * @param {MapType} mapType - The type of map being used.
 * @param {number} valueDecimals - The decimal number count for the map legend.
 * @returns {MapLegendType} The merged legend options with modifications.
 *
 * @example
 * // legend: { enabled: true, title: { text: 'Individuals per square km' } }
 * // mapType: MapType.Choropleth
 * // returns: { enabled: true, title: { text: 'Individuals per square km' } }
 *
 * @example
 * // legend: { enabled: true, title: { text: 'Individuals per square km' } }
 * // mapType: MapType.Bubble
 * // returns: { enabled: false, title: { text: 'Individuals per square km' } }
 */
const mergeLegendOptions = (legend: MapLegendType, mapType: MapType, valueDecimals: number) => {
  if (mapType === MapType.Choropleth) return { ...legend, valueDecimals };
  return {
    ...legend,
    enabled: false,
  };
};

/**
 * Converts series data for a mapbubble chart into the appropriate format.
 *
 * @param {(string | number | null)[][] | undefined} seriesData - The series data for the mapbubble chart.
 * @returns {Array<{ 'hc-key': string, z: number }>} An array of data points for the mapbubble series.
 */
const getBubbleMapData = (seriesData: (string | number | null)[][] | undefined) =>
  seriesData?.map((series) => ({
    'hc-key': series[0],
    z: typeof series[1] === 'string' ? parseInt(series[1]) : series[1],
  })) || [];

/**
 * Finds and returns the colors array based on the given color palette label from the preset palette.
 *
 * @param {ColorPaletteName} label - The label of the color palette to find.
 * @returns {Array<Palette>} The colors array corresponding to the given label. Returns an empty array if no matching palette is found.
 *
 * @example
 * // label: "Yellow-Red"
 * // returns:
 * [
 *   null, // 0 color unused
 *   null, // 1 color unused
 *   null, // 2 colors unused
 *   ['#FFEDA0', '#FEB24C', '#F03B20'], // 3 color set
 *   ['#FFFFB2', '#FECC5C', '#FD8D3C', '#E31A1C'], // 4 color set
 *   ['#FFFFB2', '#FECC5C', '#FD8D3C', '#F03B20', '#BD0026'], // 5 color set
 *   ['#FFFFB2', '#FED976', '#FEB24C', '#FD8D3C', '#F03B20', '#BD0026'], // 6 color set
 *   ['#FFFFB2', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#B10026'], // 7 color set
 *   ['#FFFFCC', '#FFEDA0', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#B10026'], // 8 color set
 *   ['#FFFFCC', '#FFEDA0', '#FED976', '#FEB24C', '#FD8D3C', '#FC4E2A', '#E31A1C', '#BD0026', '#800026'], // 9 color set
 * ];
 */
const findColorsByLabel = (label: ColorPaletteName) => {
  const selectedPreset = PRESET_PALETTE.find((preset) => preset.label === label);
  return selectedPreset ? selectedPreset.colors : [];
};

/**
 * Validates and returns the data classes for the given values based on the provided data classes.
 *
 * @param {Range[] | undefined} dataClasses - An array of data classes to be validated. If undefined, default data classes will be generated.
 * @param {(string | number)[]} values - An array of values to be used for generating data classes if they are undefined.
 * @returns {Range[]} - An array of validated data classes.
 */
const validateDataClasses = (
  dataClasses: Range[] | undefined,
  values: number[],
  colorSteps: number | undefined,
): Range[] => {
  const colorStepsCount = colorSteps === undefined ? DEFAULT_COLOR_STEPS : colorSteps;
  if (dataClasses === undefined || dataClasses.length === 0) return generateDataClasses(colorStepsCount, values);
  if (dataClasses.length !== colorSteps) return adjustDataClasses(dataClasses, colorStepsCount);
  return dataClasses;
};

/**
 * Adjusts the number of data classes based on the given color steps.
 * If the colorSteps is greater than the number of data classes, new data classes will be added.
 * If the colorSteps is less than the number of data classes, excess data classes will be removed.
 *
 * @param {Range[] | undefined} dataClasses - The current data classes, if available.
 * @param {number} colorSteps - The desired number of color steps.
 * @returns {Range[]} The updated data classes.
 */
const adjustDataClasses = (dataClasses: Range[], colorSteps: number): Range[] => {
  const diffAmount = colorSteps - dataClasses.length;
  const updatedDataClasses = cloneDeep(dataClasses);

  if (diffAmount > 0) {
    // It is possible for older dataClasses to have `to: undefined` for the last range.
    const lastDataClass = updatedDataClasses[updatedDataClasses.length - 1];
    if (lastDataClass && !lastDataClass.to) {
      // We get the previous largest value, which is commonly the last range's `from`
      const previousLargestValue = Math.max(
        ...updatedDataClasses.map((data) => {
          const fromValue = data.from !== undefined ? data.from : 0;
          const toValue = data.to !== undefined ? data.to : 0;
          return Math.max(fromValue, toValue);
        }),
      );

      lastDataClass.to = previousLargestValue + 1;
    }

    // Get the largest value from the possibly updated data classes
    const largestValue = Math.max(...updatedDataClasses.map((data) => (data.to ? data.to : 0)));

    // Add new data classes with increasing values for 'from' and 'to'
    for (let i = 0; i < diffAmount; i++) {
      updatedDataClasses.push({ from: largestValue + i, to: i === diffAmount - 1 ? undefined : largestValue + i + 1 });
    }
  } else if (diffAmount < 0) {
    // Remove excess data classes
    updatedDataClasses.splice(colorSteps);
    if (updatedDataClasses.length > 0) updatedDataClasses[updatedDataClasses.length - 1].to = undefined;
  }

  return updatedDataClasses;
};

/**
 * Get geometries from the provided map data.
 * This function prioritizes checking if mapData.objects.default.geometries is available,
 * if yes, return mapData.objects.default.geometries.
 * If not, try to check if mapData.features is available.
 * If yes, return mapData.features.
 * If not, return undefined.
 *
 * @param {ImportedMap | undefined} mapData - The map data containing geometries, if available.
 * @returns {Geometry[] | undefined} The geometries array if available, or undefined.
 */
const getGeometries = (mapData: ImportedMap | undefined): Geometry[] | undefined => {
  if (mapData?.objects?.default?.geometries) {
    return mapData.objects.default.geometries;
  } else if (mapData && mapData.features) {
    return mapData.features;
  }

  return undefined;
};

/**
 * Updates the data labels configuration for a series based on showName and showValue settings.
 *
 * @param {CustomSeriesMapOptions} series - The current series data.
 * @returns {CustomSeriesMapOptions} The updated series data with modified data labels.
 */
const getDataLabelsUpdatedSeries = (series: CustomSeriesMapOptions): CustomSeriesMapOptions => {
  const {
    dataLabels: { showName, showValue, prefix = '', suffix = '', format, enabled },
    ...remainingSeries
  } = series;

  // Design requirement: labels on maps should include prefix & suffix
  // e.g.: "Texas: $500/kg"
  const nameFormat = '{point.name}';
  const specialValueFormat = prefix + '{point.value}' + suffix;

  // We do this for older maps since they only contain `format` related info,
  // and showName and showValue were undefined previously.
  const wasShowingName = format === '{point.name}' && showName === undefined && enabled;
  const wasShowingValue = format === '{point.value}' && showValue === undefined && enabled;

  const shouldShowName = showName === true || (showName === undefined && wasShowingName);
  const shouldShowValue = showValue === true || (showValue === undefined && wasShowingValue);

  let shouldEnable = false;
  let formatString = '';

  if (shouldShowName) {
    shouldEnable = true;
    formatString += nameFormat;
  }

  if (shouldShowValue) {
    shouldEnable = true;
    formatString += shouldShowName ? `: ${specialValueFormat}` : specialValueFormat;
  }

  return {
    ...remainingSeries,
    dataLabels: {
      ...series.dataLabels,
      enabled: shouldEnable,
      format: shouldEnable ? formatString : '',
    },
  };
};

/**
 * Counts the maximum number of decimal points in an array of strings or numbers.
 *
 * @param {number[]}  values - The array of values to examine.
 * @returns {number} The maximum number of decimal points in the array.
 *
 * @example
 * const numbers = [12.34, "5.678", 9, "10.0", "7"];
 * const decimalPointCount =  countDecimalPointsInValues(numbers);
 * // decimalPointCount is 3 (from "5.678")
 */
const countDecimalPointsInValues = (values: number[]) => {
  let decimalPointCount = 0;

  for (let i = 0; i < values.length; i++) {
    // Convert the array element to a string
    const strValue = String(values[i]);

    // Use parseFloat to check if the string can be converted to a valid number
    const numericValue = parseFloat(strValue);

    // Check if the numericValue is a valid number (not NaN)
    if (!isNaN(numericValue) && strValue.includes('.')) {
      // Split the string by the decimal point and count the decimal places
      const decimalPlaces = strValue.split('.')[1].length;
      decimalPointCount = Math.max(decimalPointCount, decimalPlaces);
    }
  }

  return decimalPointCount;
};

// Exported functions are used by others, e.g.: export const func1 = () => {}
// Internal functions are exported below to be unit tested,
// they are not supposed to be imported elsewhere.
export {
  findColorsByLabel,
  mergeSeriesOptions,
  mergeMapChartColors,
  getUpdatedSeries,
  getValuesFromKeyValuePairs,
  getChartData,
  generateTableData,
  getMapRegionNames,
  getColorAxisDataClasses,
  validateDataClasses,
  adjustDataClasses,
  getGeometries,
  combineNameValueData,
  mergeLegendOptions,
  getDataLabelsUpdatedSeries,
  countDecimalPointsInValues,
};
